import { isServer } from 'solid-js/web';

/**
 * Page scroll lock for overlays (modals, the country-selector drawer, …).
 *
 * Locks on `<html>`, not `<body>`. `tailwind.css` gives `html` an `overflow-x: clip`, and once
 * the root element's overflow isn't `visible` the body's overflow stops propagating to the
 * viewport — so `body.style.overflow = 'hidden'` (the pattern this replaces) silently did
 * nothing and the page kept scrolling behind every dialog.
 *
 * Refcounted, so overlays can stack (a modal on top of the drawer) without the first one to
 * close unlocking the page for the one still open. The scrollbar's width is kept as padding
 * while locked, so the page doesn't jump sideways on browsers with classic scrollbars.
 *
 *     const release = lockPageScroll();
 *     onCleanup(release);   // idempotent — safe to call more than once
 */

let locks = 0;
let savedOverflow = '';
let savedPaddingRight = '';

export const lockPageScroll = (): (() => void) => {
    if (isServer) return () => undefined;

    const html = document.documentElement;

    if (locks === 0) {
        savedOverflow = html.style.overflow;
        savedPaddingRight = html.style.paddingRight;

        const scrollbarWidth = window.innerWidth - html.clientWidth;
        if (scrollbarWidth > 0) {
            html.style.paddingRight = `${scrollbarWidth}px`;
        }
        html.style.overflow = 'hidden';
    }
    locks++;

    let released = false;
    return () => {
        if (released) return;
        released = true;

        locks = Math.max(0, locks - 1);
        if (locks === 0) {
            html.style.overflow = savedOverflow;
            html.style.paddingRight = savedPaddingRight;
        }
    };
};
